import { Button } from '@/components/Button';
import TextField from '@/components/Input/TextField';
import Card from '@/components/Card';
import { Stack, useTheme } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination, {
  type TablePaginationProps,
} from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  ListTree,
  Pencil,
  Search,
  PlusCircle,
  Trash2,
  CircleX,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CancelBox,
  EditableBox,
  ParameterNameTypography,
  SearchButton,
  DescriptionComponent,
  ParmetersTableSearchBox,
} from './styles';
import api from '@/services/api';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import DescriptionPopper from '@/components/DescriptionPopper';
import axios from 'axios';

interface SelectedParameter extends ParameterType {
  newValue: string;
}

interface TreeModalProps {
  params: ParameterType[];
  deviceId: number;
  onRefresh?: () => void;
}

/** Tree component */
/** Displays parameters equipment management */
function Tree({ params, deviceId, onRefresh }: TreeModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  // Search and pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  // parameters data id index
  const [selectedParameters, setSelectedParameters] = useState<
    SelectedParameter[]
  >([]);

  const [currentValues, setCurrentValues] = useState<Record<number, string>>(
    {}
  );

  // Control states
  const [editingFields, setEditingFields] = useState<Record<number, boolean>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentValues((prevValues) => {
      const newValues: Record<number, string> = { ...prevValues };

      params.forEach((param) => {
        const paramId = param.id;
        const isEditing = editingFields[paramId];
        if (!paramId) return;

        if (!isEditing) {
          newValues[paramId] = param.value ?? '';
        }
      });

      return newValues;
    });
  }, [params, editingFields]);

  /**  parameters search table pagination  */
  const handleChangePage: TablePaginationProps['onPageChange'] = (
    _event,
    newPage
  ) => {
    setPage(newPage);
  };

  /** changes rows displayed per page to table  */
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (
    event
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  /** Remove changed parameters  */
  const handleRemoveSelectedParameter = useCallback((idToRemove: number) => {
    setSelectedParameters((prev) => prev.filter((p) => p.id !== idToRemove));
  }, []);

  /** filter parameters by term of search */
  const filteredParams = useMemo(() => {
    if (!debouncedSearch) return params;
    return params.filter((param) => {
      if (!param) return false;
      const lowerCaseSearchTerm = debouncedSearch.toLowerCase();
      const idMatch = String(param.id ?? '').includes(lowerCaseSearchTerm);
      const variableStr = typeof param.variable === 'object' && param.variable !== null
        ? param.variable.name ?? ''
        : String(param.variable ?? '');
      const variableMatch = variableStr
        .toLowerCase()
        .includes(lowerCaseSearchTerm);
      const nameMatch = (param.name ?? '')
        .toLowerCase()
        .includes(lowerCaseSearchTerm);
      return idMatch || variableMatch || nameMatch;
    });
  }, [params, debouncedSearch]);

  /** Change parameter input state to enable to allows edit */
  const toggleEditMode = useCallback((paramId: number, active: boolean) => {
    setEditingFields((prev) => ({
      ...prev,
      [paramId]: active,
    }));
  }, []);

  /** Change field values by input parameter entry */
  const setFieldValue = useCallback((paramId: number, value: string) => {
    setCurrentValues((prev) => ({
      ...prev,
      [paramId]: value,
    }));
  }, []);

  /** Cancel edit field value of parameter  */
  const cancelEdit = useCallback(
    (parameter: ParameterType) => {
      setCurrentValues((prev) => ({
        ...prev,
        [parameter.id]: parameter.value ?? '',
      }));

      toggleEditMode(parameter.id, false);
    },
    [toggleEditMode]
  );

  /** Validate value of changed parameter field and add to selected list to submit */
  const handleStageParameter = useCallback(
    (parameter: ParameterType) => {
      const currentValue = currentValues[parameter.id];

      if (!currentValue || currentValue.trim() === '') {
        enqueueSnackbar(t('equipment.tree.nullWarning'), {
          variant: 'warning',
        });
        return;
      }

      setSelectedParameters((prev) => {
        const filtered = prev.filter((p) => p.id !== parameter.id);
        return [...filtered, { ...parameter, newValue: currentValue }];
      });

      toggleEditMode(parameter.id, false);
    },
    [currentValues, t, toggleEditMode]
  );

  /** send changed parameters fields on the staged selected list to equipment state on server */
  const handleSubmitBatch = useCallback(async () => {
    if (selectedParameters.length === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const payload = selectedParameters.map((param) => ({
      id: param.id,
      value: param.newValue,
    }));

    try {
      const response = await api.patch(
        `/devices/${deviceId}/set-parameters`,
        payload
      );

      if (response.status >= 200 && response.status < 300) {
        enqueueSnackbar(t('equipment.tree.success'), { variant: 'success' });
        setSelectedParameters([]);
        onRefresh?.();
      } else {
        throw new Error(
          response?.data?.message || `Status: ${response.status}`
        );
      }
    } catch (error: unknown) {
      let msg = t('equipment.tree.fullErrorMessage');
      if (axios.isAxiosError(error)) {
        msg = error.response?.data?.message ?? error.message ?? msg;
      } else if (error instanceof Error) {
        msg = error.message;
      }
      setSubmitError(msg);
      enqueueSnackbar(`${t('equipment.tree.errorMessage')}: ${msg}`, {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [deviceId, onRefresh, selectedParameters, t]);

  return (
    <Stack gap="24px">
      {/* Search Container */}
      <Card sx={{ maxWidth: '100%', gap: '16px' }}>
        <Typography variant="h4">
          {t('equipment.tree.searchByParameters')}
        </Typography>
        <Stack width="100%" direction="row" gap="16px">
          <TextField
            placeholder={t('equipment.tree.placeholder')}
            sx={{ width: '100%' }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
          />
          <SearchButton variant="secondary">
            <Search size={16} />
          </SearchButton>
        </Stack>
      </Card>

      {/* Search Parameters Table */}
      <Card sx={{ maxWidth: '100%', width: '100%' }}>
        <ParmetersTableSearchBox>
          <ListTree />
          <Typography variant="h5" fontWeight={500}>
            {t('equipment.tree.fullTitle')}
          </Typography>
        </ParmetersTableSearchBox>

        <>
          <Table style={{ tableLayout: 'fixed', width: '100%' }} size="small">
            <TableHead>
              <TableRow>
                <TableCell width="10%">{t('equipment.tree.id')}</TableCell>
                <TableCell width="20%">
                  {t('equipment.tree.variable')}
                </TableCell>
                <TableCell width="40%">
                  {t('equipment.tree.parameter')}
                </TableCell>
                <TableCell width="20%">{t('equipment.tree.value')}</TableCell>
                <TableCell width="10%">{t('equipment.tree.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredParams
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((parameter) => {
                  const isEditing = !!editingFields[parameter.id];
                  const isStaged = selectedParameters.some(
                    (p) => p.id === parameter.id
                  );
                  const currentValue = currentValues[parameter.id] ?? '';

                  return (
                    <TableRow key={parameter.id}>
                      <TableCell>{parameter.id}</TableCell>
                      <TableCell>
                        {typeof parameter.variable === 'object' && parameter.variable !== null
                          ? parameter.variable.name ?? ''
                          : String(parameter.variable ?? '')}
                      </TableCell>
                      <TableCell>
                        <ParameterNameTypography>
                          {parameter.name}
                        </ParameterNameTypography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          name={`param-${parameter.id}`}
                          value={currentValue}
                          onChange={(e) =>
                            setFieldValue(parameter.id, e.target.value)
                          }
                          disabled={!isEditing || isStaged}
                          placeholder=""
                          sx={{ gap: '0px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap="4px" alignItems="center">
                          {isStaged ? (
                            <DescriptionPopper
                              description={
                                <DescriptionComponent
                                  sx={{
                                    backgroundColor: theme.palette.grey['06'],
                                  }}
                                >
                                  {t('equipment.tree.warningSelectedParameter')}
                                </DescriptionComponent>
                              }
                              placement="top"
                            >
                              <EditableBox
                                sx={{ backgroundColor: 'transparent' }}
                              >
                                <TriangleAlert
                                  color={theme.palette.secondary['01']}
                                />
                              </EditableBox>
                            </DescriptionPopper>
                          ) : (
                            parameter.writable === 1 && (
                              <>
                                {!isEditing ? (
                                  <EditableBox
                                    sx={{
                                      backgroundColor:
                                        theme.palette.primary['01'] + '30',
                                    }}
                                    onClick={() =>
                                      toggleEditMode(parameter.id, true)
                                    }
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <Pencil
                                      size={18}
                                      color={theme.palette.primary['01']}
                                    />
                                  </EditableBox>
                                ) : (
                                  <>
                                    <EditableBox
                                      onClick={() =>
                                        handleStageParameter(parameter)
                                      }
                                      role="button"
                                    >
                                      <PlusCircle
                                        size={18}
                                        color={theme.palette.success.main}
                                      />
                                    </EditableBox>
                                    <CancelBox
                                      onClick={() => cancelEdit(parameter)}
                                      role="button"
                                    >
                                      <CircleX size={18} />
                                    </CancelBox>
                                  </>
                                )}
                              </>
                            )
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={filteredParams.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      </Card>

      {/* selected parameters to change Table */}
      <Card sx={{ maxWidth: '100%' }}>
        <Stack direction="row" gap="4px" alignItems="center" marginBottom="8px">
          <ListTree />
          <Typography variant="h5" fontWeight={500}>
            {t('equipment.tree.selectedParametersTitle')} (
            {selectedParameters.length})
          </Typography>
        </Stack>

        {submitError && (
          <Typography color="error" sx={{ marginTop: '8px' }}>
            {t('equipment.tree.error')}: {submitError}
          </Typography>
        )}

        {selectedParameters.length > 0 ? (
          <>
            <Table size="small" width="100%">
              <TableHead>
                <TableRow>
                  <TableCell width="10%">{t('equipment.tree.id')}</TableCell>
                  <TableCell width="30%">
                    {t('equipment.tree.variable')}
                  </TableCell>
                  <TableCell width="40%">
                    {t('equipment.tree.newValue')}
                  </TableCell>
                  <TableCell width="20%"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedParameters.map((param) => (
                  <TableRow key={param.id}>
                    <TableCell>{param.id}</TableCell>
                    <TableCell>{param.name}</TableCell>
                    <TableCell>
                      <Typography
                        color={theme.palette.success.main}
                        fontWeight={600}
                      >
                        {param.newValue}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <CancelBox
                        sx={{
                          backgroundColor: theme.palette.error.light + '70',
                        }}
                        onClick={() => handleRemoveSelectedParameter(param.id)}
                        role="button"
                        tabIndex={0}
                      >
                        <Trash2 size={18} color={theme.palette.error.main} />
                      </CancelBox>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Stack width="95%" direction="row" justifyContent="right">
              <Button
                variant="primary"
                onClick={handleSubmitBatch}
                sx={{ marginTop: '16px' }}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? t('equipment.tree.submitting')
                  : `${t('equipment.tree.sendChanges')} (${selectedParameters.length})`}
              </Button>
            </Stack>
          </>
        ) : (
          <Typography variant="body1" color="textSecondary">
            {t('equipment.tree.noSelection')}
          </Typography>
        )}
      </Card>
    </Stack>
  );
}

export default Tree;
